import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CareerFocusWithMetrics } from '../../../hooks/useCareerFocus';
import { useDarkMode } from '../../../context/DarkModeContext';
import { TrophyIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { differenceInDays, isAfter, subWeeks, format } from 'date-fns';
import OutcomeWizard from './OutcomeWizard';

interface ResultsEmptyStateProps {
  focus: CareerFocusWithMetrics;
  onRefresh?: () => Promise<void>;
}

const ResultsEmptyState: React.FC<ResultsEmptyStateProps> = ({ focus, onRefresh }) => {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const [showWizard, setShowWizard] = useState(false);
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-gray-50';
  const textColor = darkMode ? 'text-gray-300' : 'text-gray-700';
  const mutedTextColor = darkMode ? 'text-gray-500' : 'text-gray-500';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  
  // Check if we should show the prompt to record results
  const endDate = new Date(focus.end_date);
  const today = new Date();
  const twoWeeksBeforeEnd = subWeeks(endDate, 2);
  const shouldShowRecordPrompt = isAfter(today, twoWeeksBeforeEnd) || isAfter(today, endDate);
  const daysUntilEnd = differenceInDays(endDate, today);
  const isCompleted = isAfter(today, endDate);

  const handleRecordOutcome = () => {
    // Temporarily show wizard inline instead of navigating
    setShowWizard(true);
    // navigate(`/focus/internship/${focus.id}/results/new`);
  };
  
  const handleWizardComplete = async () => {
    setShowWizard(false);
    // Refresh the results data without page reload
    if (onRefresh) {
      await onRefresh();
    }
  };
  
  const handleWizardCancel = () => {
    setShowWizard(false);
  };
  
  if (showWizard) {
    return (
      <OutcomeWizard 
        focus={focus} 
        onComplete={handleWizardComplete}
        onCancel={handleWizardCancel}
      />
    );
  }

  return (
    <div className={`${bgColor} rounded-lg p-12 text-center`}>
      <div className="max-w-md mx-auto">
        <TrophyIcon className={`w-20 h-20 mx-auto mb-6 ${mutedTextColor}`} />
        
        <h3 className={`text-xl font-semibold mb-3 ${textColor}`}>
          {isCompleted ? 'Ready to Record Your Results!' : 'Results Coming Soon'}
        </h3>
        
        {shouldShowRecordPrompt ? (
          <>
            <p className={`mb-6 ${mutedTextColor}`}>
              {isCompleted 
                ? "Your internship has ended. It's time to record your outcome and reflect on your experience."
                : `Your internship is ending soon (${daysUntilEnd} days). You can now record your outcome.`
              }
            </p>
            <button
              onClick={handleRecordOutcome}
              className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors
                ${darkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
            >
              <TrophyIcon className="w-5 h-5" />
              Record Outcome
            </button>
          </>
        ) : (
          <>
            <p className={`mb-4 ${mutedTextColor}`}>
              Your results will be available to record near the end of your internship.
            </p>
            <div className={`inline-flex items-center gap-3 px-4 py-3 rounded-lg border ${borderColor}`}>
              <CalendarIcon className="w-5 h-5" />
              <div className="text-left">
                <p className={`text-sm font-medium ${textColor}`}>
                  Recording opens in {Math.max(0, differenceInDays(twoWeeksBeforeEnd, today))} days
                </p>
                <p className={`text-xs ${mutedTextColor}`}>
                  {format(twoWeeksBeforeEnd, 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ResultsEmptyState;